import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock, FileText, PowerOff, RefreshCw, Settings2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import EinladungEinstellungen from './EinladungEinstellungen';
import MailAuftragEinstellungen from './MailAuftragEinstellungen';
import VorlagenAbgleichTest from './VorlagenAbgleichTest';
import VertragAuszugTest from './VertragAuszugTest';
import DokumentenAutomatikTest from './DokumentenAutomatikTest';
import NetzzusageTest from './NetzzusageTest';
import DealBenennungTest from './DealBenennungTest';
import KiModellVergleich from './KiModellVergleich';
import { T, feldStil, knopfStil } from '../../lib/designTokens';

const ERGEBNIS_STIL = {
  success: { farbe: '#15803D', hintergrund: '#DCFCE7', text: 'Erfolgreich' },
  skipped: { farbe: '#B45309', hintergrund: '#FEF3C7', text: 'Nichts zu tun' },
  error: { farbe: '#B91C1C', hintergrund: '#FEE2E2', text: 'Fehler' },
  // Postausgang der Mail-Auftraege (seit 22.10.2026): Versuch gescheitert, wird wiederholt.
  retry: { farbe: '#1D4ED8', hintergrund: '#DBEAFE', text: 'Wird wiederholt' },
};

function formatiereZeitpunkt(wert) {
  if (!wert) return '';
  const datum = new Date(wert);
  if (Number.isNaN(datum.getTime())) return '';
  return datum.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** "vor 3 Stunden" - macht auf einen Blick klar, ob etwas noch laeuft. */
function formatiereAbstand(wert) {
  if (!wert) return '';
  const ms = Date.now() - new Date(wert).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const minuten = Math.floor(ms / 60000);
  if (minuten < 1) return 'gerade eben';
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  const tage = Math.floor(stunden / 24);
  return tage === 1 ? 'vor 1 Tag' : `vor ${tage} Tagen`;
}

// Automatisierungen, die im Tool selbst eingerichtet oder getestet werden koennen.
const EINRICHTBAR = {
  'vermittler-einladung': true, 'vorlagen-wissensabgleich': true, 'ffpv-teaser': true,
  'recherche-auftrag': true, 'flaechenpruefung-auftrag': true, 'vertrag-auszug': true,
  'dokumenten-automatik': true, 'deal-benennung': true,
  'netzzusage-auswertung': true,
};
// Hier gibt es nichts einzurichten, nur einen Testlauf - der Knopf soll das auch sagen.
const NUR_TEST = {
  'ffpv-teaser': true, 'vertrag-auszug': true, 'dokumenten-automatik': true, 'deal-benennung': true,
};

// Als Download statt window.open: Nach dem await gilt ein neues Fenster nicht mehr als
// Klickfolge und wuerde vom Popup-Blocker geschluckt.
function pdfAusBase64Speichern(base64, dateiname) {
  const bytes = Uint8Array.from(atob(base64), (zeichen) => zeichen.charCodeAt(0));
  const adresse = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const verweis = document.createElement('a');
  verweis.href = adresse;
  verweis.download = dateiname || 'Projektteaser-Test.pdf';
  document.body.appendChild(verweis);
  verweis.click();
  verweis.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 60000);
}

/**
 * Testlauf des Projektteasers fuer EINEN Deal: Der Server erzeugt nur das PDF (kein
 * Kommentar in Bitrix, kein Phasenwechsel) und meldet Groesse und Seitenzahl.
 */
function TeaserTestlauf({ apiBase, session }) {
  const [dealId, setDealId] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  const starten = async (mitPdf) => {
    setLaeuft(true);
    setErgebnis(null);
    try {
      const token = session?.access_token;
      const antwort = await axios.post(
        `${apiBase}/automation/ffpv-teaser-webhook`,
        { test: true, dealId: dealId.trim(), mitPdf },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 120000 },
      );
      const d = antwort.data || {};
      const kb = Math.round((d.bytes || 0) / 1024);
      const bild = { nachtrag: 'hochgeladen', mapping: 'aus dem Mapping', system: 'aus Bitrix', leer: 'fehlt – Platzhalter „Luftbild folgt“' }[d.bildQuelle] || d.bildQuelle;
      setErgebnis({
        ok: d.seiten === 2,
        text: `${d.fileName} · ${kb} KB · ${d.seiten ?? '?'} Seite(n) · Bild ${bild}${d.seiten === 2 ? '' : ' – erwartet sind 2 Seiten'}`,
        hinweis: d.bildHinweis || '',
      });
      if (mitPdf && d.pdfBase64) pdfAusBase64Speichern(d.pdfBase64, d.fileName);
    } catch (error) {
      setErgebnis({ ok: false, text: error?.response?.data?.detail || error.message || 'Testlauf fehlgeschlagen.', hinweis: '' });
    } finally {
      setLaeuft(false);
    }
  };

  const bereit = Boolean(dealId.trim()) && !laeuft;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
        Erzeugt den Teaser für einen Deal genau wie die Automatik – aber ohne Kommentar in
        Bitrix und ohne Phasenwechsel. Gemeldet werden Dateiname, Größe und Seitenzahl.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={dealId}
          onChange={(e) => setDealId(e.target.value)}
          placeholder="Deal-ID, z. B. D_1234"
          aria-label="Deal-ID für den Testlauf"
          style={{ ...feldStil, width: 200 }}
        />
        <button type="button" onClick={() => starten(false)} disabled={!bereit}
          style={{ ...knopfStil(true), opacity: bereit ? 1 : 0.6 }}>
          <RefreshCw size={13} style={laeuft ? { animation: 'spin 1s linear infinite' } : undefined} />
          {laeuft ? 'Erzeugt …' : 'Testlauf'}
        </button>
        <button type="button" onClick={() => starten(true)} disabled={!bereit}
          style={{ ...knopfStil(false), opacity: bereit ? 1 : 0.6 }}>
          <FileText size={13} />
          Testlauf mit PDF
        </button>
      </div>
      {ergebnis ? (
        <div style={{
          border: `1px solid ${ergebnis.ok ? T.gutRand : T.gefahrRand}`,
          background: ergebnis.ok ? T.gutGrund : T.gefahrGrund,
          color: ergebnis.ok ? T.gut : T.gefahr,
          borderRadius: T.rChip, padding: '9px 11px', fontSize: T.tMeta, lineHeight: 1.55,
        }}>
          {ergebnis.text}
          {ergebnis.hinweis ? <div style={{ marginTop: 4, color: T.warn }}>{ergebnis.hinweis}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Schieberegler An/Aus (seit 02.10.2026). Der Server stoppt die Automatisierung an
 * ihrem Ausloeser (api/_lib/automationSchalter.js) - der Regler zeigt nicht nur an.
 */
function Schalter({ an, beschaeftigt, titel, onWechsel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={an}
      aria-label={`${titel} ${an ? 'abschalten' : 'einschalten'}`}
      title={an ? 'Abschalten' : 'Einschalten'}
      onClick={onWechsel}
      disabled={beschaeftigt}
      style={{
        marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
        cursor: beschaeftigt ? 'wait' : 'pointer', opacity: beschaeftigt ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: an ? '#15803D' : '#64748B', minWidth: 22, textAlign: 'right' }}>
        {an ? 'An' : 'Aus'}
      </span>
      <span aria-hidden="true" style={{
        position: 'relative', width: 38, height: 22, borderRadius: 999, flex: '0 0 auto',
        background: an ? '#15803D' : '#CBD5E1', transition: 'background-color .15s',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: an ? 19 : 3, width: 16, height: 16, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 2px rgba(15,23,42,.25)', transition: 'left .15s',
        }} />
      </span>
    </button>
  );
}

function Ergebnismarke({ ergebnis }) {
  const stil = ERGEBNIS_STIL[ergebnis];
  if (!stil) return null;
  return (
    <span style={{
      background: stil.hintergrund, color: stil.farbe, fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {stil.text}
    </span>
  );
}

export default function AutomationOverviewPage({ apiBase, session }) {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');
  const [offeneEinrichtung, setOffeneEinrichtung] = useState('');

  const authHeaders = useMemo(() => (
    session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
  ), [session]);

  const laden = useCallback(async (mitHinweis = false) => {
    setLaedt(true);
    setFehler('');
    try {
      const antwort = await axios.get(`${apiBase}/automation/uebersicht`, { headers: authHeaders });
      setDaten(antwort.data);
      if (mitHinweis) toast.success('Übersicht aktualisiert.');
    } catch (error) {
      const text = error?.response?.data?.detail || error.message || 'Übersicht konnte nicht geladen werden.';
      setFehler(text);
      if (mitHinweis) toast.error(text);
    } finally {
      setLaedt(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => { laden(false); }, [laden]);

  const [schaltetId, setSchaltetId] = useState('');
  const umschalten = useCallback(async (eintrag) => {
    const neu = !eintrag.eingeschaltet;
    if (!neu && !window.confirm(
      `„${eintrag.titel}“ abschalten?\n\nSolange sie aus ist, passiert bei „${eintrag.ausloeser}“ nichts. `
      + 'Wieder einschalten geht jederzeit hier.',
    )) return;
    setSchaltetId(eintrag.id);
    try {
      await axios.post(`${apiBase}/automation/uebersicht`, { op: 'schalter', automation: eintrag.id, aktiv: neu }, { headers: authHeaders });
      // Andere Server-Instanzen lesen den Schalter hoechstens 15 Sekunden zwischengespeichert.
      toast.success(neu
        ? `„${eintrag.titel}“ ist wieder an.`
        : `„${eintrag.titel}“ ist abgeschaltet – wirkt spätestens nach 15 Sekunden überall.`);
      await laden(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || 'Schalter konnte nicht gespeichert werden.');
    } finally {
      setSchaltetId('');
    }
  }, [apiBase, authHeaders, laden]);

  const bereiche = useMemo(() => {
    const liste = daten?.automatisierungen || [];
    const gruppen = new Map();
    liste.forEach((eintrag) => {
      const name = eintrag.bereich || 'Sonstiges';
      if (!gruppen.has(name)) gruppen.set(name, []);
      gruppen.get(name).push(eintrag);
    });
    return Array.from(gruppen.entries());
  }, [daten]);

  const anzahlAktiv = (daten?.automatisierungen || []).filter((a) => a.aktiv).length;
  const anzahlGesamt = (daten?.automatisierungen || []).length;
  const anzahlAus = (daten?.automatisierungen || []).filter((a) => a.eingeschaltet === false).length;

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 22, fontWeight: 800, color: '#0F172A', margin: 0 }}>
            <Activity size={22} color="#E07000" />
            Laufende Automatisierungen
          </h1>
          <p style={{ color: '#64748B', fontSize: 13, margin: '6px 0 0' }}>
            Was das Tool selbstständig erledigt – und wann es zuletzt gelaufen ist.
            {anzahlGesamt > 0 && ` ${anzahlAktiv} von ${anzahlGesamt} sind einsatzbereit.`}
            {anzahlAus > 0 && ` ${anzahlAus} abgeschaltet.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => laden(true)}
          disabled={laedt}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff',
            border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 14px',
            fontSize: 13, fontWeight: 600, color: '#334155',
            cursor: laedt ? 'default' : 'pointer', opacity: laedt ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} />
          {laedt ? 'Lädt…' : 'Aktualisieren'}
        </button>
      </div>

      {daten?.abgerufenAm && (
        <p style={{ color: '#94A3B8', fontSize: 11, margin: '0 0 20px' }}>
          Stand: {formatiereZeitpunkt(daten.abgerufenAm)}
        </p>
      )}

      {fehler && (
        <div style={{
          background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C',
          borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 20,
        }}>
          {fehler}
        </div>
      )}

      {bereiche.map(([bereich, eintraege]) => (
        <section key={bereich} style={{ marginBottom: 26 }}>
          <h2 style={{
            fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase',
            letterSpacing: '0.06em', margin: '0 0 10px',
          }}>
            {bereich}
          </h2>
          <div style={{ display: 'grid', gap: 10 }}>
            {eintraege.map((eintrag) => (
              <article
                key={eintrag.id}
                style={{
                  background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12,
                  padding: '14px 16px', display: 'grid', gap: 8,
                  // Ein Stau faellt vor allem anderen auf: eingerichtet zu sein und
                  // tatsaechlich zu laufen sind zwei verschiedene Dinge.
                  borderLeft: `4px solid ${
                    eintrag.ueberwachung?.zustand === 'stau'
                      ? '#B91C1C'
                      : (eintrag.aktiv ? '#15803D' : '#CBD5E1')
                  }`,
                  opacity: eintrag.eingeschaltet === false ? 0.85 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {eintrag.eingeschaltet === false
                    ? <PowerOff size={16} color="#64748B" />
                    : (eintrag.ueberwachung?.zustand === 'stau'
                      ? <AlertTriangle size={16} color="#B91C1C" />
                      : (eintrag.aktiv
                        ? <CheckCircle2 size={16} color="#15803D" />
                        : <AlertTriangle size={16} color="#B45309" />))}
                  <strong style={{ fontSize: 14, color: '#0F172A' }}>{eintrag.titel}</strong>
                  {eintrag.eingeschaltet === false && (
                    <span style={{
                      background: '#E2E8F0', color: '#334155', fontSize: 11, fontWeight: 700,
                      padding: '2px 8px', borderRadius: 999,
                    }}>
                      abgeschaltet
                    </span>
                  )}
                  {eintrag.eingeschaltet !== false && !eintrag.aktiv && (
                    <span style={{
                      background: '#F1F5F9', color: '#64748B', fontSize: 11, fontWeight: 700,
                      padding: '2px 8px', borderRadius: 999,
                    }}>
                      nicht einsatzbereit
                    </span>
                  )}
                  {eintrag.ueberwachung?.zustand === 'stau' && (
                    <span style={{
                      background: '#FEE2E2', color: '#991B1B', fontSize: 11, fontWeight: 700,
                      padding: '2px 8px', borderRadius: 999,
                    }}>
                      läuft nicht
                    </span>
                  )}
                  {eintrag.schalter ? (
                    <Schalter
                      an={eintrag.eingeschaltet !== false}
                      // Alle Regler sperren, solange einer speichert: Zwei Schaltvorgaenge
                      // zugleich koennten sich in der gemeinsamen Ablage ueberschreiben.
                      beschaeftigt={Boolean(schaltetId)}
                      titel={eintrag.titel}
                      onWechsel={() => umschalten(eintrag)}
                    />
                  ) : null}
                  {!eintrag.schalter && eintrag.nichtSchaltbar ? (
                    <span title={eintrag.nichtSchaltbar} style={{ marginLeft: 'auto', fontSize: 12, color: '#94A3B8' }}>
                      nicht hier schaltbar
                    </span>
                  ) : null}
                </div>

                {eintrag.eingeschaltet === false && (
                  <div style={{
                    background: '#F1F5F9', color: '#334155', fontSize: 12,
                    padding: '8px 10px', borderRadius: 8, lineHeight: 1.5,
                  }}>
                    Abgeschaltet – der Auslöser wird übersprungen, bis sie wieder an ist.
                    {eintrag.schalter?.geaendertAm
                      ? ` Seit ${formatiereZeitpunkt(eintrag.schalter.geaendertAm)}${eintrag.schalter.geaendertVon ? ` (${eintrag.schalter.geaendertVon})` : ''}.`
                      : ''}
                  </div>
                )}

                <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                  {eintrag.beschreibung}
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748B' }}>
                  <Zap size={12} />
                  <span>Startet: {eintrag.ausloeser}</span>
                </div>

                {eintrag.hinweis && (
                  <div style={{
                    background: '#FEF3C7', color: '#92400E', fontSize: 12,
                    padding: '8px 10px', borderRadius: 8,
                  }}>
                    {eintrag.hinweis}
                  </div>
                )}

                {/* Stauwaechter: meldet liegengebliebene Arbeit. Ein abgewiesener
                    Webhook hinterlaesst weder Auftrag noch Fehler - ohne diese
                    Zeile bliebe ein Ausfall unsichtbar, so wie am 09.09.2026. */}
                {eintrag.ueberwachung?.zustand === 'stau' && (
                  <div style={{
                    background: '#FEE2E2', color: '#991B1B', fontSize: 12,
                    padding: '8px 10px', borderRadius: 8, lineHeight: 1.5,
                  }}>
                    <strong>Hier bleibt Arbeit liegen. </strong>
                    {eintrag.ueberwachung.text}
                  </div>
                )}

                {/* Ungeprueft ist ausdruecklich NICHT dasselbe wie in Ordnung. */}
                {eintrag.ueberwachung?.zustand === 'ungeprueft' && (
                  <div style={{
                    background: '#F8FAFC', color: '#64748B', fontSize: 12,
                    padding: '8px 10px', borderRadius: 8, border: '1px dashed #CBD5E1',
                  }}>
                    {eintrag.ueberwachung.text}
                  </div>
                )}

                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  borderTop: '1px solid #F1F5F9', paddingTop: 8, fontSize: 12, color: '#475569',
                }}>
                  <Clock size={12} />
                  {eintrag.letzteAktivitaet ? (
                    <>
                      <span>
                        Zuletzt: {formatiereZeitpunkt(eintrag.letzteAktivitaet.zeitpunkt)}
                        {formatiereAbstand(eintrag.letzteAktivitaet.zeitpunkt)
                          && ` (${formatiereAbstand(eintrag.letzteAktivitaet.zeitpunkt)})`}
                      </span>
                      <Ergebnismarke ergebnis={eintrag.letzteAktivitaet.ergebnis} />
                      {eintrag.letzteAktivitaet.text && (
                        <span style={{ color: '#64748B' }}>{eintrag.letzteAktivitaet.text}</span>
                      )}
                    </>
                  ) : (
                    // Bewusst ehrlich: kein Protokoll heisst nicht "nichts passiert".
                    <span style={{ color: '#94A3B8' }}>
                      Führt kein eigenes Protokoll – Läufe sind hier nicht sichtbar.
                    </span>
                  )}
                  {eintrag.zusatz && <span style={{ color: '#64748B' }}>· {eintrag.zusatz}</span>}
                  {/* Auch im Normalfall zeigen, dass ueberhaupt gemessen wird -
                      sonst ist "kein Alarm" nicht von "nicht geprueft" zu trennen. */}
                  {(eintrag.ueberwachung?.zustand === 'ok' || eintrag.ueberwachung?.zustand === 'ruht') && (
                    <span style={{ color: '#94A3B8' }}>
                      · {eintrag.ueberwachung.wartende
                        ? `${eintrag.ueberwachung.wartende} wartend, überwacht`
                        : 'überwacht, nichts offen'}
                    </span>
                  )}
                </div>

                {EINRICHTBAR[eintrag.id] && (
                  <>
                    <button
                      type="button"
                      onClick={() => setOffeneEinrichtung(offeneEinrichtung === eintrag.id ? '' : eintrag.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, alignSelf: 'flex-start',
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, color: '#E07000', fontFamily: 'inherit',
                      }}
                    >
                      <Settings2 size={13} />
                      {NUR_TEST[eintrag.id]
                        ? (offeneEinrichtung === eintrag.id ? 'Test schließen' : 'Testen')
                        : (offeneEinrichtung === eintrag.id ? 'Einrichtung schließen' : 'Einrichten')}
                      {offeneEinrichtung === eintrag.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>

                    {offeneEinrichtung === eintrag.id && (
                      <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: 14, marginTop: 2 }}>
                        {eintrag.id === 'ffpv-teaser' ? (
                          <TeaserTestlauf apiBase={apiBase} session={session} />
                        ) : eintrag.id === 'vertrag-auszug' ? (
                          <VertragAuszugTest apiBase={apiBase} session={session} onFertig={() => laden(false)} />
                        ) : eintrag.id === 'dokumenten-automatik' ? (
                          <DokumentenAutomatikTest apiBase={apiBase} session={session} onFertig={() => laden(false)} />
                        ) : eintrag.id === 'netzzusage-auswertung' ? (
                          <NetzzusageTest apiBase={apiBase} session={session} onFertig={() => laden(false)} />
                        ) : eintrag.id === 'deal-benennung' ? (
                          <DealBenennungTest apiBase={apiBase} session={session} onFertig={() => laden(false)} />

                        ) : (eintrag.id === 'recherche-auftrag' || eintrag.id === 'flaechenpruefung-auftrag') ? (
                          <MailAuftragEinstellungen
                            key={eintrag.id}
                            art={eintrag.id}
                            apiBase={apiBase}
                            session={session}
                            onGespeichert={() => laden(false)}
                          />
                        ) : eintrag.id === 'vorlagen-wissensabgleich' ? (
                          <VorlagenAbgleichTest
                            apiBase={apiBase}
                            session={session}
                            onFertig={() => laden(false)}
                          />
                        ) : (
                          <EinladungEinstellungen
                            apiBase={apiBase}
                            session={session}
                            onGespeichert={() => laden(false)}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </article>
            ))}
          </div>
        </section>
      ))}

      {/* Kein Automatismus, aber eine Einstellung fuer Admins: welches KI-Modell
          das Gespraech im Vertragsstudio nutzt, samt Vergleich (seit 24.10.2026). */}
      <section style={{ marginTop: 30 }}>
        <h2 style={{
          fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase',
          letterSpacing: '0.06em', margin: '0 0 10px',
        }}>
          KI im Gespräch
        </h2>
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '14px 16px' }}>
          <KiModellVergleich apiBase={apiBase} session={session} />
        </div>
      </section>

      {(daten?.letzteLaeufe || []).length > 0 && (
        <section style={{ marginTop: 30 }}>
          <h2 style={{
            fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase',
            letterSpacing: '0.06em', margin: '0 0 10px',
          }}>
            Letzte Läufe
          </h2>
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
            {daten.letzteLaeufe.map((lauf, index) => (
              <div
                key={`${lauf.zeitpunkt}-${index}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                  borderTop: index === 0 ? 'none' : '1px solid #F1F5F9', fontSize: 12,
                }}
              >
                <span style={{ color: '#94A3B8', minWidth: 118, fontVariantNumeric: 'tabular-nums' }}>
                  {formatiereZeitpunkt(lauf.zeitpunkt)}
                </span>
                <Ergebnismarke ergebnis={lauf.ergebnis} />
                <span style={{ color: '#334155', flex: 1 }}>{lauf.text}</span>
                {lauf.dealId && <span style={{ color: '#94A3B8' }}>Deal {lauf.dealId}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {!laedt && !fehler && anzahlGesamt === 0 && (
        <p style={{ color: '#64748B', fontSize: 13 }}>Keine Automatisierungen gefunden.</p>
      )}
    </div>
  );
}
